import React from 'react';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { FormikProvider, Form, useFormik } from 'formik';
import {
  Grid,
  Button,
  Dialog,
  MenuItem,
  TextField,
  DialogTitle,
  DialogContent
} from '@mui/material';
import { CloseIcon } from '@/common/icons/CloseIcon';
import { MIconButton } from '../../../../../../../@material-extend';

import CONFIG from '@/config';
import axios from 'axios';
import { getRestHeaders, callRestPatchEndpoint } from '@/common/utils/api';
import { DateInput } from '@/components/DateInput';

interface CustomerData {
  id: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  mobilePhone?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  photoUrl?: string;
}

interface ExtendedCredit {
  id: number;
  quantity: number;
  source: string;
  description: string;
  expires: string;
  created_at: string;
  original_expiry?: string;
  is_refunded?: boolean;
  studio_id?: string;
  authorized_studio_ids?: string[];
  purchase_store_id?: number;
  // Additional properties used in CreditExpModel
  issue_date?: string;
  purchased_line_item?: string;
  credit_type?: string;
  ids?: number[];
}

interface CreditExpModelProps {
  selectedData: ExtendedCredit | null;
  isModalOpen: boolean;
  creditExpModelClose: () => void;
  creditUpdateSuccess: () => void;
  currentUserStoreId?: string;
  customerAllData: CustomerData;
}

interface FormValues {
  customer: number;
  quantity: number;
  expires: string;
  createdAt: string;
  source: string;
  newExpires: string;
  purchasedLineItem?: string;
  creditType?: string;
}

const CreditExpModel = (props: CreditExpModelProps) => {
  const {
    isModalOpen,
    selectedData,
    customerAllData,
    creditExpModelClose,
    creditUpdateSuccess
  } = props;
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [expireNow, setExpireNow] = React.useState(false);

  const credits = [];
  for (let i = 1; i <= selectedData?.quantity; i++) {
    credits.push(i);
  }

  const initialValues: FormValues = {
    customer: customerAllData?.id,
    quantity: selectedData?.quantity || 0,
    expires: selectedData?.expires || '',
    createdAt: selectedData?.issue_date || '',
    source: selectedData?.source || '',
    newExpires: selectedData?.expires || '',
    purchasedLineItem: selectedData?.purchased_line_item,
    creditType: selectedData?.credit_type
  };

  const formik = useFormik({
    initialValues,
    validationSchema: null,
    enableReinitialize: true,
    onSubmit: async (values: FormValues) => {
      try {
        const payload = {
          customerCreditIds: selectedData?.ids || [selectedData?.id],
          quantity: values.quantity,
          expires: values.newExpires,
          expiresNow: expireNow
        };

        await callRestPatchEndpoint(payload, '/staff/credits');

        enqueueSnackbar('Credits Expiration updated!', {
          variant: 'success',
          action: (key: any) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)}>
              <CloseIcon />
            </MIconButton>
          )
        });
        creditUpdateSuccess();
        creditExpModelClose();
      } catch (error) {
        enqueueSnackbar('Credits Expiration update failed!', {
          variant: 'error'
        });
      }
    }
  });

  const { handleSubmit, isSubmitting } = formik;

  return (
    <Dialog
      open={isModalOpen}
      onClose={creditExpModelClose}
      maxWidth="sm"
      fullWidth={true}
    >
      <FormikProvider value={formik}>
        <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
          <DialogTitle
            style={{
              color: 'white',
              padding: '10px',
              textAlign: 'center',
              backgroundColor: '#003f5c'
            }}
          >
            Edit Credit Expiration Date
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} pt={3}>
              <Grid item xs={2}>
                <TextField
                  select
                  fullWidth
                  name="quantity"
                  label="Quantity"
                  defaultValue={credits?.length || ''}
                  InputLabelProps={{ shrink: true, required: true }}
                  onChange={e =>
                    formik.setFieldValue('quantity', e?.target?.value)
                  }
                >
                  {credits.length > 0 &&
                    credits.map(cr => (
                      <MenuItem key={`${cr}`} value={cr}>
                        {cr}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
              <Grid item xs={10}>
                <DateInput
                  fullWidth
                  variant="outlined"
                  name="newExpires"
                  label="Select Next Expiration Date"
                  InputLabelProps={{ shrink: true, required: true }}
                  inputProps={{ min: moment().format('YYYY-MM-DD') }}
                  onChange={e =>
                    formik.setFieldValue('newExpires', e?.target?.value)
                  }
                  value={moment(formik?.values?.newExpires).format(
                    'YYYY-MM-DD'
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <div
            style={{
              width: '100%',
              padding: '10px',
              display: 'flex',
              background: '#fafafa',
              justifyContent: 'space-between'
            }}
          >
            <Button
              type="submit"
              color="primary"
              variant="contained"
              style={{ marginRight: '5px' }}
              onClick={() => setExpireNow(true)}
            >
              Expire Immediately
            </Button>
            <div
              style={{
                justifyContent: 'flex-end'
              }}
            >
              <Button
                type="button"
                color="primary"
                variant="outlined"
                style={{ marginRight: '5px' }}
                onClick={creditExpModelClose}
              >
                Close
              </Button>
              <LoadingButton
                type="submit"
                color="primary"
                variant="contained"
                loading={isSubmitting}
              >
                Save
              </LoadingButton>
            </div>
          </div>
        </Form>
      </FormikProvider>
    </Dialog>
  );
};

export default CreditExpModel;
